import { useState } from "react";
import { apiFetch } from "@/lib/api-client";

/**
 * D1: 10DLC Brand Registration Form
 * Legal name, EIN, vertical, website, contact info.
 * Submit to TCR via Telnyx API. Status tracking.
 */

const VERTICALS = [
  "Professional Services",
  "Real Estate",
  "Healthcare",
  "Education",
  "Retail",
  "Hospitality",
  "Financial Services",
  "Technology",
  "Other",
] as const;

interface BrandData {
  id: string;
  businessName: string;
  country: string;
  status: string;
  vettingScore: number | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
}

export default function BrandRegistrationPage() {
  const [brand, setBrand] = useState<BrandData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [businessName, setBusinessName] = useState("");
  const [ein, setEin] = useState("");
  const [country, setCountry] = useState("US");
  const [vertical, setVertical] = useState("");
  const [website, setWebsite] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await apiFetch<{ data: BrandData }>("/v1/sms-registration/brands", {
        method: "POST",
        body: JSON.stringify({
          businessName,
          ein: ein || undefined,
          country,
          vertical: vertical || undefined,
          website: website || undefined,
          contactEmail: contactEmail || undefined,
          contactPhone: contactPhone || undefined,
        }),
      });
      setBrand(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit brand registration");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (brand) {
    return (
      <div>
        <h1 className="mb-6 text-2xl font-bold">10DLC Brand Registration</h1>
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-3">
            <StatusBadge status={brand.status} />
            <h2 className="text-lg font-semibold">{brand.businessName}</h2>
          </div>
          <dl className="grid gap-2 text-sm">
            <div className="flex gap-2">
              <dt className="font-medium text-muted-foreground">Country:</dt>
              <dd>{brand.country}</dd>
            </div>
            {brand.vettingScore !== null && (
              <div className="flex gap-2">
                <dt className="font-medium text-muted-foreground">Vetting Score:</dt>
                <dd>{brand.vettingScore}/100</dd>
              </div>
            )}
            {brand.rejectionReason && (
              <div className="rounded-md bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                Rejection reason: {brand.rejectionReason}
              </div>
            )}
            {brand.submittedAt && (
              <div className="flex gap-2">
                <dt className="font-medium text-muted-foreground">Submitted:</dt>
                <dd>{new Date(brand.submittedAt).toLocaleDateString()}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">10DLC Brand Registration</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Register your business with The Campaign Registry (TCR) to send SMS messages in the US.
        This is required for A2P 10DLC messaging.
      </p>

      {error && (
        <div className="mb-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-medium">Business Information</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Legal Business Name *</label>
              <input required value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">EIN (Tax ID)</label>
              <input value={ein} onChange={(e) => setEin(e.target.value)} placeholder="XX-XXXXXXX" className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Country *</label>
              <select required value={country} onChange={(e) => setCountry(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Vertical</label>
              <select value={vertical} onChange={(e) => setVertical(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm">
                <option value="">Select vertical...</option>
                {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Website</label>
              <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 font-medium">Contact Information</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Contact Email</label>
              <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Contact Phone</label>
              <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        <button type="submit" disabled={isSubmitting || !businessName} className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {isSubmitting ? "Submitting to TCR..." : "Submit Brand Registration"}
        </button>
      </form>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
    verified: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    rejected: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
    suspended: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors[status] ?? "bg-[var(--color-muted)]"}`}>
      {status}
    </span>
  );
}
